import { cn } from '@/shared/utils';
import { CopyButton } from './CopyButton';
import { type ReactNode, useRef } from 'react';
import { QrCode } from '@/shared/components/QrCode.tsx';
import { useMediaQuery } from '@/shared/lib';

interface CopyFieldProps {
  label?: string;
  value: string;
  className?: string;
  labelClassName?: string;
  variant?: 'default' | 'compact' | 'list-item';
  qrCodeData?: string;
  actions?: ReactNode;
}

export function CopyField({
  label,
  value,
  className,
  variant,
  labelClassName,
  qrCodeData,
  actions,
}: CopyFieldProps) {
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const isMobile = useMediaQuery('(max-width: 480px)');

  if (variant === 'compact') {
    return (
      <div className="flex justify-between gap-[1rem] flex-no-wrap">
        <span className="headline4 text-primary break-all">{value}</span>
        <CopyButton
          value={value}
          size="md"
          variant="secondary"
          buttonClassName="p-0"
        />
      </div>
    );
  }

  const handleParentClick = () => {
    if (copyButtonRef.current) {
      copyButtonRef.current.click();
    }
  };

  if (variant === 'list-item') {
    return (
      <div
        onClick={handleParentClick}
        className={cn(
          'group flex flex-col justify-between rounded-[1.6rem] rounded-[1.2rem] px-[2.4rem] py-[0.8rem] min-h-[5.6rem] w-full gap-[0.2rem]',
          'hover:bg-secondary-bg cursor-pointer'
        )}>
        <div className="flex items-center justify-between">
          {label && (
            <span className={cn('text-secondary p4', labelClassName)}>
              {label}
            </span>
          )}
        </div>
        <div className="flex gap-[1rem] flex-no-wrap">
          <span className="headline4 text-primary break-all flex-1">
            {value}
          </span>

          <CopyButton
            ref={copyButtonRef}
            value={value}
            size="md"
            variant="secondary"
            buttonClassName="p-0"
            iconClassName="group-hover:text-primary/70 text-primary"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-row w-full gap-[1.6rem]">
      <div
        className={cn(
          'pt-[1rem] pb-[1.6rem] px-[2.4rem] bg-primary rounded-[1.6rem]',
          className
        )}>
        <div className="flex items-center justify-between">
          {label && (
            <span
              className={cn(
                'p3 text-muted-foreground break-all',
                labelClassName
              )}>
              {label}
            </span>
          )}
          <div className="flex items-center gap-[0.4rem]">
            {actions}
            <CopyButton
              value={value}
              size="lg"
              variant="secondary"
              buttonClassName="size-[4rem] p-[0.8rem] [&_svg]:text-white"
              iconClassName="text-white dark:text-primary-foreground group-hover:text-muted-foreground dark:group-hover:text-muted-foreground"
            />
          </div>
        </div>
        <div>
          <span className="headline4 text-primary-foreground p2 break-all">
            {value}
          </span>
        </div>
      </div>
      {!isMobile && qrCodeData && <QrCode data={qrCodeData} />}
    </div>
  );
}
